import React, { useEffect, useMemo, useState } from 'react';
import { Route, useLocation, useNavigate, Routes as Switch } from 'react-router-dom';
import { useFirebase } from '../hooks/firebase';

import TopNavBar from '../components/Navigation/TopNavBar';

import AnonUserRouter from '../components/Router/AnonUserRouter';
import ToastHandler from '../components/Toast/ToastHandler';
import * as ROUTES from '../constants/routes';
import { useApi } from '../hooks/use-api';
import { ProvideViewPreferences } from '../hooks/view-preferences';
import { sendPageView } from '../utils/googleAnalytics';
import { useUser } from '../hooks/use-user';
import DationInstanceUserRouter from '../components/Router/DationInstanceUserRouter';
import AdminRouter from '../components/Router/AdminRouter';
import MaintenancePage from './MaintenancePage';
import companyUserRouter from '../components/Router/CompanyUserRouter';

function MainPage() {
	const firebase = useFirebase();
	const navigate = useNavigate();
	const location = useLocation();
	const {
		profile,
		activeCompany,
		isPortalAdmin,
		isCompanyAdmin,
		isCompanySuperUser,
		isSchoolAdmin,
		isSchoolSuperUser,
	} = useUser();
	const [isLoading, setIsLoading] = useState(true);

	const [, verifyProfile] = useApi.get(ROUTES.VERIFY_PROFILE, {}, { lazy: true });

	useEffect(
		() =>
			firebase.onAuthStateChanged(firebase.auth, async (user) => {
				if (user) {
					await firebase.initializeIdToken();
					if (firebase.userSignUp) {
						await firebase.doPasswordUpdate(firebase.userPassword);
						await verifyProfile();
						await navigate(ROUTES.LANDING);
						firebase.setUserPassword(null);
						firebase.setUserSignUp(false);
					} else {
						// Initialize firebase user and token
						await firebase.setAuthUser(user);
					}
				} else if (firebase.authUser !== null) {
					firebase.setAuthUser(null);
				}
				setIsLoading(false);
			}),
		[firebase.userSignUp],
	);

	const handleSignOut = () => {
		firebase.doSignOut();
	};

	useEffect(() => {
		sendPageView(location);
	}, [location]);

	const router = useMemo(() => {
		let routerComponent;
		if (isPortalAdmin) {
			routerComponent = AdminRouter({ handleSignOut });
		} else if (isSchoolAdmin || isSchoolSuperUser) {
			routerComponent = DationInstanceUserRouter({ isSuperUser: isSchoolSuperUser, handleSignOut });
		} else if (isCompanyAdmin || isCompanySuperUser) {
			routerComponent = companyUserRouter({ isSuperUser: isCompanySuperUser, handleSignOut });
		} else {
			routerComponent = (
				<Switch>
					<Route exact path={ROUTES.MAINTENANCE} element={<MaintenancePage />} />
				</Switch>
			);
		}
		return (
			// Adding the key ensures all pages get re-rendered when the company id changes
			// this ensures all the data being correct without having to fix every single page
			<div className="container-fluid pb-0" key={activeCompany?.id}>
				{routerComponent}
			</div>
		);
	}, [profile, activeCompany]);

	if (isLoading) {
		return <div className="loading-spinner" />;
	}

	return firebase.authUser ? (
		<ProvideViewPreferences>
			<TopNavBar />
			<ToastHandler />
			{router}
		</ProvideViewPreferences>
	) : (
		<div className="auth-wrapper">
			<div className="auth-inner">
				<AnonUserRouter />
			</div>
		</div>
	);
}

export default MainPage;
